import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  message,
  Spin,
  Typography,
  Space,
  Alert,
  Statistic,
  Progress,
  List,
  Avatar,
  Tag,
  Divider,
} from 'antd';
import {
  CloudOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  FolderOutlined,
  FileOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { testAccConnection, getAccHubs, getAccProjects } from '../../../services/api';
import { ProtectedRoute, PageGuard } from '../../../components/common/security/ProtectedRoute';

const { Title, Text } = Typography;

interface ConnectionStatus {
  success: boolean;
  message?: string;
  token_available?: boolean;
  error?: string;
  error_type?: string;
}

interface DashboardStats {
  totalHubs: number;
  totalProjects: number;
  activeProjects: number;
}

const AccDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [hubs, setHubs] = useState<any[]>([]);
  const [hubsLoading, setHubsLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalHubs: 0,
    totalProjects: 0,
    activeProjects: 0,
  });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);

  useEffect(() => {
    testConnection();
  }, []);

  // 005-TKF projesini bul ve otomatik yönlendir
  useEffect(() => {
    if (recentProjects.length > 0) {
      const tkfProject = recentProjects.find(
        (p) => p.attributes?.name?.includes('005-TKF')
      );
      if (tkfProject) {
        // Session storage'a kaydet ve yönlendir
        sessionStorage.setItem(
          'selectedProject',
          JSON.stringify({
            id: tkfProject.id,
            name: tkfProject.attributes?.name,
            hubName: tkfProject.hubName,
            hubId: tkfProject.hubId,
          })
        );
        navigate(`/design/acc/projects/${tkfProject.id}?hubId=${tkfProject.hubId}`);
      }
    }
  }, [recentProjects, navigate]);

  const testConnection = async () => {
    setLoading(true);
    try {
      const result = await testAccConnection();
      setConnectionStatus(result);
      if (result.success && result.token_available) {
        await loadDashboardData();
      } else {
        const errorMsg = result.error || result.message || 'Bilinmeyen hata';
        const errorType = (result as any).error_type || 'unknown';

        if (errorType === 'ssl_error') {
          message.error(`SSL Bağlantı Hatası: ${errorMsg}`);
        } else if (errorType === 'config_error') {
          message.error(`Yapılandırma Hatası: ${errorMsg}`);
        } else {
          message.error(`Bağlantı Hatası: ${errorMsg}`);
        }
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.message || 'Bilinmeyen hata';
      const errorType = error.response?.data?.error_type || 'unknown';

      setConnectionStatus({
        success: false,
        message: errorMsg,
        error: errorMsg,
        error_type: errorType,
      });

      if (errorType === 'ssl_error') {
        message.error(`SSL Bağlantı Hatası: ${errorMsg}`);
      } else {
        message.error('Bağlantı testi başarısız: ' + errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    setHubsLoading(true);
    try {
      // Load hubs
      const hubsResult = await getAccHubs();
      if (hubsResult.success && hubsResult.data) {
        const hubsData = hubsResult.data.data || [];
        setHubs(hubsData);

        // Load projects for each hub
        let totalProjects = 0;
        const allProjects: any[] = [];

        for (const hub of hubsData) {
          try {
            const projectsResult = await getAccProjects(hub.id);
            if (projectsResult.success && projectsResult.data) {
              const projects = projectsResult.data.data || [];
              totalProjects += projects.length;

              // Add hub info to projects
              projects.forEach((project: any) => {
                allProjects.push({
                  ...project,
                  hubName: hub.attributes?.name,
                  hubId: hub.id,
                });
              });
            }
          } catch (err) {
            console.error(`Hub ${hub.id} projeleri yüklenirken hata:`, err);
          }
        }

        // Sort by last modified and take recent 5
        const sortedProjects = allProjects
          .sort((a, b) => {
            const dateA = new Date(a.attributes?.extension?.data?.lastModifiedTime || 0);
            const dateB = new Date(b.attributes?.extension?.data?.lastModifiedTime || 0);
            return dateB.getTime() - dateA.getTime();
          })
          .slice(0, 5);

        setRecentProjects(sortedProjects);

        setStats({
          totalHubs: hubsData.length,
          totalProjects,
          activeProjects: totalProjects, // Assuming all are active
        });
      }
    } catch (error: any) {
      message.error('Dashboard verileri yüklenirken hata: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setHubsLoading(false);
    }
  };

  const handleProjectClick = (project: any) => {
    sessionStorage.setItem(
      'selectedProject',
      JSON.stringify({
        id: project.id,
        name: project.attributes?.name,
        hubName: project.hubName,
        hubId: project.hubId,
      })
    );
    navigate(`/design/acc/projects/${project.id}?hubId=${project.hubId}`);
  };

  return (
    <ProtectedRoute>
      <PageGuard>
        <div style={{ padding: '24px' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <Title level={2} style={{ margin: 0 }}>
                  <CloudOutlined style={{ marginRight: 12 }} />
                  Autodesk Construction Cloud
                </Title>
                <Text type="secondary">ACC entegrasyonu ve proje yönetimi</Text>
              </div>
              <Button icon={<ReloadOutlined />} onClick={testConnection} loading={loading}>
                Yenile
              </Button>
            </div>

            {/* Connection Status */}
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space size="large">
                  {loading ? (
                    <Spin />
                  ) : connectionStatus?.success ? (
                    <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                  ) : (
                    <CloseCircleOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
                  )}
                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      Bağlantı Durumu
                    </Title>
                    <Text type={connectionStatus?.success ? 'success' : 'danger'}>
                      {connectionStatus?.success ? 'Bağlantı başarılı' : 'Bağlantı hatası'}
                    </Text>
                  </div>
                </Space>
                {connectionStatus?.success && (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Token Mevcut
                  </Tag>
                )}
              </div>

              {connectionStatus && !connectionStatus.success && (
                <Alert
                  message="Bağlantı Hatası"
                  description={
                    <div>
                      <div>{connectionStatus.message || 'Bir bağlantı hatası oluştu'}</div>
                      {connectionStatus.error && (
                        <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                          Detay: {connectionStatus.error}
                        </div>
                      )}
                      {connectionStatus.error_type === 'ssl_error' && (
                        <div style={{ marginTop: 8, fontSize: '12px' }}>
                          💡 SSL sertifika sorunu olabilir. Backend konsol loglarını kontrol edin.
                        </div>
                      )}
                    </div>
                  }
                  type="error"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginTop: 16 }}
                />
              )}
            </Card>

            {/* Stats */}
            {connectionStatus?.success && (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Toplam Hub"
                      value={stats.totalHubs}
                      prefix={<CloudOutlined style={{ color: '#1890ff' }} />}
                      loading={hubsLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Toplam Proje"
                      value={stats.totalProjects}
                      prefix={<FolderOutlined style={{ color: '#52c41a' }} />}
                      loading={hubsLoading}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8}>
                  <Card>
                    <Statistic
                      title="Aktif Proje"
                      value={stats.activeProjects}
                      prefix={<FileOutlined style={{ color: '#722ed1' }} />}
                      loading={hubsLoading}
                    />
                  </Card>
                </Col>
              </Row>
            )}

            {/* Hubs and Recent Projects */}
            {connectionStatus?.success && (
              <Row gutter={[16, 16]}>
                {/* Hubs */}
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <CloudOutlined />
                        <span>Hub'lar</span>
                        <Tag>{hubs.length}</Tag>
                      </Space>
                    }
                    extra={
                      <Button
                        type="link"
                        onClick={() => navigate('/design/acc/projects')}
                        icon={<RightOutlined />}
                      >
                        Tümünü Gör
                      </Button>
                    }
                    loading={hubsLoading}
                  >
                    {hubs.length > 0 ? (
                      <List
                        dataSource={hubs}
                        renderItem={(hub: any) => (
                          <List.Item>
                            <List.Item.Meta
                              avatar={
                                <Avatar
                                  style={{ backgroundColor: '#1890ff' }}
                                  icon={<CloudOutlined />}
                                />
                              }
                              title={hub.attributes?.name || hub.id}
                              description={
                                <Space>
                                  <Tag color="blue">{hub.attributes?.region || 'Unknown'}</Tag>
                                  <Text type="secondary">{hub.type || 'Hub'}</Text>
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Alert
                        message="Hub bulunamadı"
                        description="Henüz hub yok veya erişim izniniz bulunmuyor."
                        type="info"
                      />
                    )}
                  </Card>
                </Col>

                {/* Recent Projects */}
                <Col xs={24} lg={12}>
                  <Card
                    title={
                      <Space>
                        <FolderOutlined />
                        <span>Son Projeler</span>
                      </Space>
                    }
                    extra={
                      <Button
                        type="link"
                        onClick={() => navigate('/design/acc/projects')}
                        icon={<RightOutlined />}
                      >
                        Tümünü Gör
                      </Button>
                    }
                    loading={hubsLoading}
                  >
                    {recentProjects.length > 0 ? (
                      <List
                        dataSource={recentProjects}
                        renderItem={(project: any) => (
                          <List.Item
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleProjectClick(project)}
                          >
                            <List.Item.Meta
                              avatar={
                                <Avatar
                                  style={{ backgroundColor: '#52c41a' }}
                                >
                                  {(project.attributes?.name || 'P').charAt(0).toUpperCase()}
                                </Avatar>
                              }
                              title={project.attributes?.name || 'İsimsiz Proje'}
                              description={
                                <Space>
                                  <Tag>{project.hubName}</Tag>
                                  {project.attributes?.extension?.data?.lastModifiedTime && (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                                      {new Date(
                                        project.attributes.extension.data.lastModifiedTime
                                      ).toLocaleDateString('tr-TR')}
                                    </Text>
                                  )}
                                </Space>
                              }
                            />
                            <RightOutlined style={{ color: '#bfbfbf' }} />
                          </List.Item>
                        )}
                      />
                    ) : (
                      <Alert
                        message="Proje bulunamadı"
                        description="Henüz proje yok veya erişim izniniz bulunmuyor."
                        type="info"
                      />
                    )}
                  </Card>
                </Col>
              </Row>
            )}

            {/* Quick Actions */}
            {connectionStatus?.success && (
              <Card title="Hızlı İşlemler">
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={8}>
                    <Button
                      type="primary"
                      block
                      size="large"
                      icon={<FolderOutlined />}
                      onClick={() => navigate('/design/acc/projects')}
                    >
                      Projeleri Görüntüle
                    </Button>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Button
                      block
                      size="large"
                      icon={<FileOutlined />}
                      onClick={() => message.info('Doküman yönetimi yakında...')}
                    >
                      Dokümanlar
                    </Button>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Button
                      block
                      size="large"
                      icon={<TeamOutlined />}
                      onClick={() => message.info('Takım yönetimi yakında...')}
                    >
                      Takım
                    </Button>
                  </Col>
                </Row>
              </Card>
            )}
          </Space>
        </div>
      </PageGuard>
    </ProtectedRoute>
  );
};

export default AccDashboard;
